import { Injectable, inject, Injector } from '@angular/core';
import { HttpClient, HttpContext, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable, from } from 'rxjs';
import { AuthService } from './auth.service';
import { TenantSelectionService } from './tenant-selection.service';
import { Router } from '@angular/router';
type QueryParams = Record<string, string | number | boolean>;

/**
 * Generic API service for making HTTP requests to Firebase Functions.
 * Handles authentication headers and App Check tokens.
 */
@Injectable({
  providedIn: 'root',
})
export class ApiService {
  private http = inject(HttpClient);
  private authService = inject(AuthService);
  private injector = inject(Injector);

  /**
   * Get TenantSelectionService lazily to avoid circular dependency
   */
  private get tenantSelectionService(): TenantSelectionService {
    return this.injector.get(TenantSelectionService);
  }

  /**
   * Get authentication headers with Firebase Auth token
   */
  private async getAuthHeaders(): Promise<HttpHeaders> {
    const headers = new HttpHeaders();
    const token = await this.authService.getIdToken();

    if (token) {
      return headers.set('Authorization', `Bearer ${token}`);
    }

    return headers;
  }

  /**
   * Get App Check token header (placeholder for now)
   */
  private getAppCheckHeaders(headers: HttpHeaders, appCheckToken?: string): HttpHeaders {
    // TODO: Implement App Check token retrieval
    // For now, return headers as-is
    if (appCheckToken) {
      return headers.set('X-App-Check-Token', appCheckToken);
    }
    return headers;
  }

  /**
   * Make a GET request
   * Automatically adds tenantId from TenantSelectionService
   */
  async get<T>(baseUrl: string, url: string, params?: QueryParams): Promise<Observable<T>> {
    const tenantId = this.tenantSelectionService.getSelectedTenantId();
    if (!tenantId) {
      throw new Error('Tenant ID is required');
    }

    let httpParams = new HttpParams();
    // Always add tenantId first
    httpParams = httpParams.set('tenantId', tenantId);

    if (params) {
      Object.keys(params).forEach((key) => {
        if (params[key] !== undefined && params[key] !== null) {
          httpParams = httpParams.set(key, params[key].toString());
        }
      });
    }

    const headers = await this.getAuthHeaders();
    const finalHeaders = this.getAppCheckHeaders(headers);

    return this.http.get<T>(`${baseUrl}${url}`, {
      headers: finalHeaders,
      params: httpParams,
    });
  }

  /**
   * Make a POST request
   * Automatically adds tenantId from TenantSelectionService to request body
   * @param context Optional HttpContext (e.g. SKIP_GLOBAL_LOADING for requests with their own overlay)
   */
  async post<TResponse, TBody extends object>(
    baseUrl: string,
    url: string,
    body: TBody,
    context?: HttpContext,
  ): Promise<Observable<TResponse>> {
    const tenantId = this.tenantSelectionService.getSelectedTenantId();
    if (!tenantId) {
      throw new Error('Tenant ID is required');
    }

    // Add tenantId to body
    const bodyWithTenant = { ...body, tenantId };

    const headers = await this.getAuthHeaders();
    const finalHeaders = this.getAppCheckHeaders(headers.set('Content-Type', 'application/json'));

    return this.http.post<TResponse>(`${baseUrl}${url}`, bodyWithTenant, {
      headers: finalHeaders,
      observe: 'body',
      ...(context && { context }),
    });
  }

  /**
   * Make a PUT request
   * Automatically adds tenantId from TenantSelectionService to request body
   */
  async put<TResponse, TBody extends object>(
    baseUrl: string,
    url: string,
    body: TBody,
  ): Promise<Observable<TResponse>> {
    const tenantId = this.tenantSelectionService.getSelectedTenantId();
    if (!tenantId) {
      throw new Error('Tenant ID is required');
    }

    // Add tenantId to body
    const bodyWithTenant = { ...body, tenantId };

    const headers = await this.getAuthHeaders();
    const finalHeaders = this.getAppCheckHeaders(headers.set('Content-Type', 'application/json'));

    return this.http.put<TResponse>(`${baseUrl}${url}`, bodyWithTenant, {
      headers: finalHeaders,
    });
  }

  /**
   * Make a PATCH request
   * Automatically adds tenantId from TenantSelectionService to request body
   */
  async patch<TResponse, TBody extends object>(
    baseUrl: string,
    url: string,
    body: TBody,
  ): Promise<Observable<TResponse>> {
    const tenantId = this.tenantSelectionService.getSelectedTenantId();
    if (!tenantId) {
      throw new Error('Tenant ID is required');
    }

    const bodyWithTenant = { ...body, tenantId };

    const headers = await this.getAuthHeaders();
    const finalHeaders = this.getAppCheckHeaders(headers.set('Content-Type', 'application/json'));

    return this.http.patch<TResponse>(`${baseUrl}${url}`, bodyWithTenant, {
      headers: finalHeaders,
    });
  }

  /**
   * Make a DELETE request
   * Automatically adds tenantId from TenantSelectionService to query params.
   * Optional extraParams are merged into query params (e.g. workspaceInstanceId).
   */
  async delete<T>(baseUrl: string, url: string, extraParams?: QueryParams): Promise<Observable<T>> {
    const tenantId = this.tenantSelectionService.getSelectedTenantId();
    if (!tenantId) {
      throw new Error('Tenant ID is required');
    }

    const headers = await this.getAuthHeaders();
    const finalHeaders = this.getAppCheckHeaders(headers);

    let httpParams = new HttpParams();
    httpParams = httpParams.set('tenantId', tenantId);
    if (extraParams) {
      Object.keys(extraParams).forEach((key) => {
        if (extraParams[key] !== undefined && extraParams[key] !== null) {
          httpParams = httpParams.set(key, extraParams[key].toString());
        }
      });
    }

    return this.http.delete<T>(`${baseUrl}${url}`, {
      headers: finalHeaders,
      params: httpParams,
    });
  }
}
